import {
  CLIENT_CARE_EMAIL,
  ERROR_ODOMETER_INVALID_INPUT,
  ERROR_VIN_INVALID_INPUT_MESSAGE,
  ERROR_VIN_INVALID_LENGTH_MESSAGE,
  ERROR_VIN_REQUIRED_MESSAGE,
  GENERAL_ERROR_MESSAGE
} from 'common/constants'
import { ISystemSetting } from 'types/baseTypes'
import { validate } from 'utils/vehicleUtils'
import * as yup from 'yup'
import { IVehicleInfo } from '../vehicleTypes'

// const vinRegex = /^([A-Z\d]{3})[A-Z]{2}\d{2}([A-Z\d]{1})([X\d]{1})([A-Z\d]{3})\d{5}$/
const vinRegex = /[a-zA-Z0-9]/
const odometerRegex = /^[0-9]+$/
export interface IGroundedVehicleInfo extends IVehicleInfo {
  GroundingCompletionDate: string
  TimeLeft: string
}

export const enum DispositionOption {
  'EMPLOYEE_RETURN' = 12,
  'LESSEE_RETURN' = 13,
  'DEALER_INVENTORY_PURCHASE' = 14,
  'EMPLOYEE_PURCHASE' = 15,
  'LESSEE_PURCHASE' = 16,
  'DEALER_INVENTORY_PURCHASE_WITH_NEW_NMAC_CONTRACT' = 17
}

export interface IVinSearchForm {
  VIN: string
}

export const initialVinValues: IVinSearchForm = {
  VIN: ''
}

export const initialValues: IVinSearchForm = {
  VIN: ''
}
export interface ILocationDropDown {
  ID: number
  Description: string
  Address1: string
  Address2: string
  City: string
  State: string
  StateCode: string
  Zip: string
  AddressTypeID: number
}

export interface IGroundingForm {
  vehicleReturnDate: Date
  currentVIN: string
  odometerReading: string
  confirmOdometerReading: string
  newVIN?: string
  newBrand?: string
  additionalComment?: string
  requiresNewVIN: boolean
  requiresNewVINNotBrand: boolean
  requiresNewVINForCampaign: boolean
  vinValidationList: ISystemSetting
  campaign?: number
}

export interface IAddressForm {
  address: string
  address2: string
  city: string
  zip: string
  stateCode: string
  countryCode: string
}

export const initialAddressValue: IAddressForm = {
  address: '',
  address2: '',
  city: '',
  zip: '',
  stateCode: '-1',
  countryCode: '-1'
}

// export const initialGroundingValue: IGroundingForm = {
//   vehicleReturnDate: null,
//   currentVIN: '',
//   odometerReading: '',
//   confirmOdometerReading: '',
//   newVIN: '',
//   newBrand: '',
//   requiresNewVIN: '',
//   requiresNewVINNotBrand: 'true',
//   vinValidationList: {} as ISystemSetting,
//   campaign: -1
// }

export const addressValidateSchema = yup.object({
  address: yup.string().trim().required(GENERAL_ERROR_MESSAGE),
  city: yup.string().trim().required(GENERAL_ERROR_MESSAGE),
  zip: yup.string().trim().required(GENERAL_ERROR_MESSAGE),
  stateCode: yup.string().notOneOf(['-1', '0'], GENERAL_ERROR_MESSAGE)
})

export const SRPAddressValidateSchema = yup.object({
  address: yup.string().trim().required(GENERAL_ERROR_MESSAGE),
  city: yup.string().trim().required(GENERAL_ERROR_MESSAGE),
  zip: yup.string().trim().required(GENERAL_ERROR_MESSAGE),
  stateCode: yup.string().notOneOf(['-1', '0'], GENERAL_ERROR_MESSAGE),
  countryCode: yup.string().notOneOf(['-1', '0'], GENERAL_ERROR_MESSAGE)
})

export const vinValidateSchema = yup.object({
  vinNumber: yup
    .string()
    .trim()
    .required(ERROR_VIN_REQUIRED_MESSAGE)
    .min(17, ERROR_VIN_INVALID_LENGTH_MESSAGE)
    .max(17, ERROR_VIN_INVALID_LENGTH_MESSAGE)
    .matches(vinRegex, ERROR_VIN_INVALID_INPUT_MESSAGE)
})

export const groundingValueValidateSchema = (
  values: IGroundingForm,
  getLocalText: (key: string, defaultText?: string) => string
) => {
  return yup.object({
    vehicleReturnDate: yup.string().required(GENERAL_ERROR_MESSAGE),
    requiresNewVIN: yup.string().trim(),
    currentVIN: yup.string().matches(vinRegex, ERROR_VIN_INVALID_INPUT_MESSAGE),
    odometerReading: yup
      .string()
      .trim()
      .required(GENERAL_ERROR_MESSAGE)
      .matches(odometerRegex, ERROR_ODOMETER_INVALID_INPUT),
    confirmOdometerReading: yup
      .string()
      .trim()
      .required(GENERAL_ERROR_MESSAGE)
      .matches(odometerRegex, ERROR_ODOMETER_INVALID_INPUT)
      .oneOf([yup.ref('odometerReading'), null], 'The odometer readings do not match.'),
    newVIN: yup
      .string()
      .trim()
      .when(['requiresNewVIN', 'requiresNewVINNotBrand', 'requiresNewVINForCampaign'], {
        is: (requiresNewVIN, requiresNewVINNotBrand, requiresNewVINForCampaign) =>
          (requiresNewVIN === 'true' && requiresNewVINNotBrand === true) || requiresNewVINForCampaign === true,
        then: (yup) =>
          yup
            .required(ERROR_VIN_REQUIRED_MESSAGE)
            .min(17, ERROR_VIN_INVALID_INPUT_MESSAGE)
            .max(17, ERROR_VIN_INVALID_INPUT_MESSAGE)
            .test(
              'is-same-grounding-vin',
              'You have entered the VIN being grounded. Please enter the New VIN.',
              (newVIN) => {
                return newVIN.toUpperCase() !== values.currentVIN.toUpperCase()
              }
            )
            .test((newVIN, context) => {
              const firstThreeChars = newVIN.substring(0, 3)
              const isInvalidNisInfVin =
                values.vinValidationList.Value === '1' &&
                values.vinValidationList.ValueString.split(',').indexOf(firstThreeChars) === -1
              if (isInvalidNisInfVin) {
                return context.createError({
                  message: `VIN must be a Nissan or INFINITI vehicle, please re-enter valid VIN. You may also contact Customer Care directly at 1-866-RPM-0333 or 1-866-776-0333, or email ${getLocalText(
                    CLIENT_CARE_EMAIL,
                    'RPMsupport@coxautoinc.com'
                  )} with any questions.`
                })
              }
              if (!validate(newVIN))
                return context.createError({
                  message: `You have entered an invalid VIN. Please re-enter. You may also contact Customer Care directly at 1-866-RPM-0333 or 1-866-776-0333, or email ${getLocalText(
                    CLIENT_CARE_EMAIL,
                    'RPMsupport@coxautoinc.com'
                  )} with any questions.`
                })
              else return true
            }),
        otherwise: (yup) => yup.notRequired()
      }),
    newBrand: yup.string().trim()
  })
}
